<template>
	<el-row>
		<el-col :span="24">
			<div class="base-form header">
				<h2 class="item" v-if="isCreatingUserView">
					Новый пользователь <span>{{ruleForm?.login}}</span>
				</h2>
				<h2 class="item" v-else>
					Карточка пользователя <span>{{user?.login}}</span>
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24">
			<div class="base-form form">
				<el-form :model="ruleForm" ref="addUserForm" status-icon :rules="rules">
					<el-form-item v-if="!isCreatingUserView" label="Дата создания" label-position="top">
						<el-input v-model="ruleForm.date" disabled />
					</el-form-item>
					
					<el-form-item v-if="!isCreatingUserView" label="Логин" label-position="top">
						<el-input v-model="ruleForm.login" disabled />
					</el-form-item>

					<el-form-item v-if="isCreatingUserView" label="Логин" label-position="top" prop="login">
						<el-input v-model="ruleForm.login" />
					</el-form-item>

					<el-form-item label="Пароль" label-position="top" prop="password">
						<el-input v-model="ruleForm.password" type="password" show-password />
					</el-form-item>

					<el-form-item label="Повторите пароль" label-position="top" prop="passwordConfirmation">
						<el-input v-model="ruleForm.passwordConfirmation" type="password" />
					</el-form-item>

					<el-form-item v-if="!isCreatingUserView && user?.employee" label="Сотрудник" label-position="top">			
						<el-link type="primary">
							{{ user?.employee?.lastname }} {{ user?.employee?.firstname?.substring(0,1) }}
						</el-link>
						., {{ user?.employee?.position?.name }}
					</el-form-item>

					<el-form-item v-if="isCreatingUserView || (!isCreatingUserView && !user?.employee)" label="Сотрудник" label-position="top" prop="employee">
						<catalog-picker-component
							ref="m"
							auth
							v-model="ruleForm.employee"
							placeholder="Поиск по инициалам, телефону и email" 
							search-url="/api/employees"
							:catalog-tab="employeesCatalogTab"								
							@catalog-item-picked="onEmployeePickedFromCatalog">
							<template #label>								
								{{ formatEmployeeLabel() }}
							</template>
							<template #default="scope">
								<div>
									<span style="float: left">{{ scope.firstname }} {{ scope.lastname }}</span>
									<span style="float: right; color: var(--el-text-color-secondary); font-size: .8rem">
										{{ scope.phone }}
									</span>
								</div>
							</template>
						</catalog-picker-component>
					</el-form-item>

					<el-form-item label="Роли" label-position="top">
						<el-select
							multiple
							v-model="ruleForm.roles"
							placeholder="Выберите из списка"
						>
							<el-option
								v-for="item in userRoles"
								:key="item.id"
								:value="item.id"
								:label="item.name">
							</el-option>
						</el-select>
					</el-form-item>

					<el-form-item v-if="!isCreatingUserView">
						<el-checkbox v-model="ruleForm.blocked" label="Пользователь заблокирован" size="large" />
					</el-form-item>

					<el-form-item v-if="!isCreatingUserView" label="Причина блокировки" label-position="top" prop="blockedReason">
						<el-input
							:disabled="!ruleForm.blocked"
							v-model="ruleForm.blockedReason"
							maxlength="5000"
							show-word-limit
							type="textarea"
							placeholder="Укажите причину блокировки (опционально)"
						/>
					</el-form-item>	
					
					<el-form-item>
						<el-button type="primary" @click="onUpdateUserClick" v-if="!isCreatingUserView">
							Сохранить
						</el-button>
						<el-button type="primary" @click="onCreateUserClick" v-if="isCreatingUserView">
							Создать
						</el-button>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification } from 'element-plus';
import { date as dateFormatter } from '@/helpers/formatters';
import { Reactive, reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import EmployeeModel from '@/models/EmployeeModel';
import UserRoleModel from '@/models/UserRoleModel';
import UserModel from '@/models/UserModel';
import EventHelper from '@/helpers/eventsHelper';
import TabsDictionary from '@/variables/tabs';

@Options({
	components: {
		ElButton, ElTable, ElLink, CatalogPickerComponent
	},
	props: {
		params: Array
	}
})
export default class UserView extends Vue {
	/** params */
	params!: Array<any>;
	user: UserModel | null = null;
	isCreatingUserView = false;

	/** constants */
	employeesCatalogTab = TabsDictionary.employees;
	
	/** view-models */		
	employees: Array<EmployeeModel> = [];
	userRoles: Array<UserRoleModel> = store.getters.userRoles;

	ruleForm = reactive({
		date: '',
		login: '',

		password: null,
		passwordConfirmation: null,

		employee: null,

		roles: [],
		
		blocked: false,
		blockedReason: null
	});

	rules: Reactive<any> = null;
	
	mounted() {
		this.initForm();

		this.$nextTick()
			.then(() => {	
				this.rules = reactive({
					login: [
						{ required: true, message: 'Логин не может быть пуст'},
						{ min: 3, max: 255, message: 'Логин не может быть короче 3 и длинее 255 символов'},
						{ validator: this.loginAvailabilityValidator, message: 'Такой логин уже существует'},
					],
					password: [			
						{ required: this.isCreatingUserView, validator: (a: any, b: any, c: any) => this.passwordLengthValidator(a,b,c), trigger: 'blur' }
					],
					passwordConfirmation: [
						{ required: this.isCreatingUserView, validator: (a: any, b: any, c: any) => this.passwordsEqualValidator(a,b,c), trigger: 'blur' }
					],
					employee: [
						{ required: true, message: 'Выберите сотрудника для связи с пользователем', trigger: 'blur' }
					]
				});
			});
	}

	/**
	 * init form with user's data from incoming parameters array (params)
	 */
	initForm() {
		if (!this.params?.length) {
			this.isCreatingUserView = true;
			return;
		}

		this.user = this.params[0]?.user as UserModel | null;		

		if (this.user?.id) {
			this.isCreatingUserView = false;
			this.employees = [ this.user.employee! ];
			this.ruleForm.date = dateFormatter.dateTime(this.user.date, true, true);
			this.ruleForm.login = this.user.login;
			this.ruleForm.employee = this.user.employee as any;
			this.ruleForm.roles = this.user.roles?.map((i: any) => i?.role?.id) as any;	
			this.ruleForm.blocked = this.user.blocked;	
			this.ruleForm.blockedReason = this.user.blockReason as any;

			this.setEmployeePickerValue(new EmployeeModel(this.user.employee));
		}
		else {
			this.isCreatingUserView = true;
		}
	}

	async onUpdateUserClick() {		
		const form = this.$refs.addUserForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				const jwt = store.getters.jwt;
				const a = axiosHelper.createInstance(jwt);
				
				try {
					const request = {
						id: this.user?.id,
						login: this.user?.login,
						password: this.ruleForm.password,
						passwordConfirmation: this.ruleForm.passwordConfirmation,
						isBlocked: this.ruleForm.blocked,
						blockingReason: this.ruleForm.blockedReason,
						roles: this.ruleForm.roles
					};
					const response = await a.put('/api/users', request);
					if (response.data?.id) {
						ElNotification({
							message: `Пользователь '${this.user!.login}' обновлен`,
							type: 'success',
						});
						this.closeCurrentTab();
						EventHelper.dispatchEvent('usersUpdate');
					} else {												
						throw new Error('Unexpected response');
					}
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							title: 'Ошибка',
							message: `Ошибка соединения, не удалось сохранить данные`,
							type: 'error',
						});
					} else {
						ElNotification({
							title: 'Ошибка',
							message: `Ошибка сохранения данных пользователя`,
							type: 'error',
						});
					}
					console.error('Error updating order', e);
				}
			}
		});
	}

	async onCreateUserClick() {
		const form = this.$refs.addUserForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				const jwt = store.getters.jwt;
				const a = axiosHelper.createInstance(jwt);
				
				try {
					const employeeId = (this.ruleForm.employee as any).id;

					const request = {						
						login: this.ruleForm.login,
						password: this.ruleForm.password,
						passwordConfirmation: this.ruleForm.passwordConfirmation,
						roles: this.ruleForm.roles,
						employeeId
					};
					const response = await a.post('/api/users', request);
					if (response.data?.id) {
						ElNotification({
							message: `Пользователь '${this.ruleForm.login}' успешно создан`,
							type: 'success',
						});
						this.closeCurrentTab();
						EventHelper.dispatchEvent('usersUpdate');
					} else {												
						throw new Error('Unexpected response');
					}
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							title: 'Ошибка',
							message: `Ошибка соединения, не удалось создать нового пользователя`,
							type: 'error',
						});
					} else {
						ElNotification({
							title: 'Ошибка',
							message: `Ошибка создания нового пользователя`,
							type: 'error',
						});
					}
					console.error('Error creating user', e);
				}

			}
		});
	}

	onEmployeePickedFromCatalog(employee: EmployeeModel) {
		this.setEmployeePickerValue(employee);
	}
		
	passwordLengthValidator(rule: any, value: string, callback: Function) {
		if (this.isCreatingUserView ||
			(!this.isCreatingUserView && value)) {
			if (!value ||
				value.length < 5) {
				callback(new Error('Длина пароля не может быть меньше 5 и длиннее 255 символов'));
				return false;
			}
		}

		return true;
	}

	passwordsEqualValidator(rule: any, value: string, callback: Function) {
		if (this.isCreatingUserView ||
			(!this.isCreatingUserView && this.ruleForm.password)) {				
			if (this.ruleForm.passwordConfirmation != this.ruleForm.password) {
				callback(new Error('Пароли не совпадают'));
				return false;
			}
		}

		return true;
	}

	async loginAvailabilityValidator(rule: any, value: string, callback: any) : Promise<boolean> {
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
			
		try {
			await a.get(`/api/users/login_avail/${value}`);
			return true;
		} catch (e) {
			callback(new Error(`Логин '${value}' уже используется`));
			return false;
		}
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	formatEmployeeLabel() {
		const e = this.ruleForm.employee ? this.ruleForm.employee as EmployeeModel : null;
		if (!e) {
			return '';
		}

		return `${e.lastname} ${e.firstname?.substring(0,1)}., ${e.position?.name?.toLowerCase()}`;
	}

	setEmployeePickerValue(value: EmployeeModel) {
		this.ruleForm.employee = value as any;
		this.employees = [ value ];
		const employeePickerComponent = this.$refs.m as CatalogPickerComponent;
		employeePickerComponent.set(value);
	}
}
</script>


<style scoped>
h2.item span {
	color: var(--el-color-primary);
}
</style>